import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Badge from '../../../components/ui/Badge'
import Like from '../../../components/ui/Like'
import AddToClosetButton from '../../../components/ui/AddToClosetButton'
import Button from '../../../components/ui/Button'
import QuantitySelector from '../../../components/ui/QuantitySelector'
import ValidationMessage from '../../../components/ui/ValidationMessage'
import RequestFromDesignerModal from '../../../components/listings/RequestFromDesignerModal'
import AuthRequiredModal from '../../../components/common/AuthRequiredModal'
import { selectCurrentUser } from '../../../redux/slices/authSlice'

import SwapActions from './tabs/SwapActions'
import DeliveryMethodSelection from './shared/DeliveryMethodSelection'
import { formatCurrency } from '../../../utils/formatters'
import { setUseEstimator, setSelectedRate } from '../../../redux/slices/deliverySlice'
import { setCurrentProduct } from '../../../redux/slices/productSlice'
import { useGetAddressesQuery } from '../../../redux/apis'
import { limitRentalOptions } from '../../../utils/rentalPriceUtils'

const ProductActions = ({ product, onAddToCart, onMessageMerchant, className = '' }) => {
  const dispatch = useDispatch()
    const currentUser = useSelector(selectCurrentUser)
  const token = useSelector(state => state.auth?.token)

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authActionType, setAuthActionType] = useState('perform this action')
  const [activeTab, setActiveTab] = useState(() => {
    if (product?.forSell) return 'buy'
    if (product?.forRent) return 'rent'
    if (product?.openToSwap) return 'swap'
    return 'buy'
  })

  // Store the product in Redux when component loads
  useEffect(() => {
    if (product) {
      dispatch(setCurrentProduct(product));

      // Set delivery option for virtual products
      if (product.productType === 'virtual') {
        setDeliveryOption('virtual');
      }
    }
  }, [product, dispatch]);

  // Shipping states
  const [selectedShippingRate, setSelectedShippingRate] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [deliveryOption, setDeliveryOption] = useState(null)

  // Rental selection state
  const [selectedRentOption, setSelectedRentOption] = useState(null)

  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    deliveryMethod: false,
    rentalPeriod: false
  })

  // Custom request modal state
  const [isCustomRequestModalOpen, setIsCustomRequestModalOpen] = useState(false)

  // Helper to check auth before an action
  const requireAuth = (actionLabel) => {
    if (!currentUser) {
      setAuthActionType(actionLabel)
      setShowAuthModal(true)
      return false
    }
    return true
  }



  const handleQuantityChange = (newQuantity) => {
    setQuantity(Math.max(1, Math.min(parseInt(stock), newQuantity)));
  };

  // Get user addresses
  const { data: userAddresses = [], isLoading: isLoadingAddresses } =
    useGetAddressesQuery(undefined, {
      skip: !useSelector(state => state.auth?.isAuthenticated)
    })

  // Get shipping state from Redux
  const shippingRates = useSelector(
    state => state.shipping?.shippingRates || null
  )
  const isCalculatingRates = useSelector(
    state => state.shipping?.loading || false
  )
  const shippingError = useSelector(state => state.shipping?.error || null)

  // Reset selected rate when shipping rates change
  useEffect(() => {
    if (
      shippingRates &&
      Array.isArray(shippingRates) &&
      shippingRates.length > 0
    ) {
      // Default to the cheapest rate
      const cheapestRate = shippingRates.reduce((prev, current) =>
        parseFloat(prev.amount) < parseFloat(current.amount) ? prev : current
      )
      setSelectedShippingRate(cheapestRate)
    } else {
      setSelectedShippingRate(null)
    }
  }, [shippingRates])

  // Clear validation errors when selections are made
  useEffect(() => {
    if (deliveryOption) {
      setValidationErrors(prev => ({ ...prev, deliveryMethod: false }))
    }
  }, [deliveryOption])

  useEffect(() => {
    if (selectedRentOption) {
      setValidationErrors(prev => ({ ...prev, rentalPeriod: false }))
    }
  }, [selectedRentOption])

  // Limit rental options to maximum 3 - must be called before early return
  const limitedRentalOptions = useMemo(() => {
    if (!product || !product.rentPriceAndTime) return [];
    return limitRentalOptions(product.rentPriceAndTime, 3);
  }, [product?.rentPriceAndTime]);

  if (!product) return null

  const {
    forSell,
    forRent,
    openToSwap,
    price = 0,
    currency = 'USD',
    stock = 0,
    productType,
    shippingAddress,
    pickupAddress,
    useShippingForPickup,
    parcelInfo,
    openToShipping,
    openToPickUp
  } = product

  const isVirtual = productType === 'virtual'

  // Determine which address to use for shipping origin
  const originAddress = openToShipping
    ? shippingAddress || (useShippingForPickup ? pickupAddress : null)
    : null

  // Handle shipping address selection - now simplified to just trigger estimator
  const handleAddressSelect = address => {
    // Force estimator mode for shipping
    dispatch(setUseEstimator(true));

    // If address has forceEstimate flag, that means we're using simplified flow
    if (address && address.forceEstimate) {
      // Create an estimated rate
      const estimatedRate = {
        provider: 'Estimated',
        serviceName: 'Standard',
        amount: '5.99',
        currency: 'USD',
        days: '1-3',
        isEstimate: true
      };
      dispatch(setSelectedRate(estimatedRate));
    }
  };

  // Handle quantity change
  // const handleQuantityChange = (newQuantity) => {
  //   setQuantity(newQuantity);
  // };

  // Group badges by type
  const typeBadges = [
    forSell && 'sale',
    forRent && 'rent',
    (!forSell && !forRent) && 'concept',
    productType === 'virtual' && 'virtual'
  ].filter(Boolean)

  return (
    <div className={`space-y-6 max-w-2xl mx-auto ${className}`}>
      {/* Name, Badges, and Like Button */}
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          {/* Left side: Add to Closet button */}
          <AddToClosetButton listingId={product.id} />

          <div className="flex-1"></div>

          {/* Right side: Like button */}
          <Like productId={product.id} size='md' showCount={true} />
        </div>
        {typeBadges.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {typeBadges.map(type => (
              <Badge key={type} type={type} />
            ))}
          </div>
        )}
      </div>

      {/* Tab Buttons for Buy and Rent */}
      {forSell && forRent && (
        <div className="flex bg-gray-50 p-1 rounded-xl mb-4 max-w-2xl mx-auto border border-gray-100">
          <button
            className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'buy' ? 'bg-white shadow-sm text-gray-900 border border-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('buy')}
          >
            Buy
          </button>
          <button
            className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'rent' ? 'bg-white shadow-sm text-gray-900 border border-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('rent')}
          >
            Rent
          </button>
        </div>
      )}

      {/* Pricing Display */}
      {(
        <div className='space-y-4 w-full max-w-2xl'>
          {/* Buy Section */}
          {forSell && activeTab === 'buy' && (
            <div className='bg-gray-50 p-4 rounded-lg'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium text-gray-700'>Purchase Price</span>
                <span className='text-xl font-semibold text-gray-900'>
                  {formatCurrency(price, currency)}
                </span>
              </div>
            </div>
          )}

          {/* Rent Section */}
          {forRent && activeTab === 'rent' && limitedRentalOptions && limitedRentalOptions.length > 0 && (
            <div className='space-y-3'>
              <label className="block text-sm font-medium text-gray-700">
                Choose a period {limitedRentalOptions.length < (product.rentPriceAndTime?.length || 0) && (
                  <span className="text-xs text-gray-500 ml-1">
                    (showing {limitedRentalOptions.length} of {product.rentPriceAndTime?.length})
                  </span>
                )}
              </label>
              <div className='grid grid-cols-1 gap-3'>
                {limitedRentalOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (selectedRentOption === option) {
                        setSelectedRentOption(null);
                      } else {
                        setSelectedRentOption(option);
                      }
                    }}
                    className={`flex items-center justify-between w-full p-4 rounded-xl border-2 transition-colors ${
                      selectedRentOption === option 
                        ? 'border-brand-teal bg-teal-50/10' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span className="text-base font-medium text-gray-900">
                      {option.number} {option.option}{option.number > 1 ? 's' : ''}
                    </span>
                    <span className="text-base font-bold text-gray-900">{formatCurrency(option.price, option.currency || currency)}</span>
                  </button>
                ))}
              </div>

              {/* Security Deposit Display */}
              {product.rentDeposit && product.rentDeposit > 0 && (
                <div className="mt-4 flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      {formatCurrency(product.rentDeposit, currency)} refundable deposit
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">returned on safe return</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quantity Selector - Show only for physical products */}
      {!isVirtual && (
        <div className="w-full max-w-2xl pt-2 pb-1">
          <QuantitySelector
            value={quantity}
            onChange={handleQuantityChange}
            stock={stock}
            min={1}
            max={10}
            showAvailable={true}
            disabled={stock <= 0}
            inline={true}
          />
        </div>
      )}

      {/* Delivery Method Selection - Show for physical products */}
      {!isVirtual && (openToShipping || openToPickUp) && (
        <div className='w-full max-w-2xl'>
          <DeliveryMethodSelection
            openToShipping={openToShipping}
            openToPickUp={openToPickUp}
            deliveryOption={deliveryOption}
            setDeliveryOption={setDeliveryOption}
            pickUpAddress={pickupAddress}
            shippingCost={5.99}
            currency={currency}
            shippingRate={selectedShippingRate}
            isShippingRateSelected={!!selectedShippingRate}
            onAddressSelect={handleAddressSelect}
            product={product}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className='w-full max-w-2xl mx-auto'>
        {/* Beta Mode Buttons */}
        

        {/* Action Button - Only when Launched and Transactions Enabled */}
        {(
          <div className='flex gap-3 flex-col sm:flex-row'>
            {/* Main Action (Buy/Rent) */}
            <div className='flex-1'>
              {activeTab === 'buy' && forSell && (
                <Button
                  variant="orange"
                  className="w-full h-full"
                  onClick={() => {
                    if (!requireAuth('purchase items')) return;
                    if (!isVirtual && !deliveryOption) {
                      setValidationErrors(prev => ({ ...prev, deliveryMethod: true }));
                      return;
                    }
                    onAddToCart?.({
                      type: 'buy',
                      quantity: isVirtual ? 1 : quantity,
                      deliveryMethod: isVirtual ? 'virtual' : deliveryOption,
                      shippingRate: selectedShippingRate,
                      navigateToCart: true
                    });
                  }}
                >
                  Buy Now
                </Button>
              )}

              {activeTab === 'rent' && forRent && (
                <Button
                  variant="teal"
                  className="w-full h-full"
                  onClick={() => {
                    if (!requireAuth('rent items')) return;
                    if (!selectedRentOption) {
                      setValidationErrors(prev => ({ ...prev, rentalPeriod: true }));
                      return;
                    }

                    if (!isVirtual && !deliveryOption) {
                      setValidationErrors(prev => ({ ...prev, deliveryMethod: true }));
                      return;
                    }

                    const rentalDetails = {
                      duration: selectedRentOption.option === 'day' ? selectedRentOption.number :
                        selectedRentOption.option === 'week' ? selectedRentOption.number * 7 :
                          selectedRentOption.option === 'month' ? selectedRentOption.number * 30 : 0,
                      option: selectedRentOption.option,
                      number: selectedRentOption.number,
                      price: selectedRentOption.price
                    };

                    onAddToCart?.({
                      type: 'rent',
                      quantity: 1,
                      rentalPeriod: rentalDetails,
                      deliveryMethod: isVirtual ? 'virtual' : deliveryOption,
                      shippingRate: selectedShippingRate,
                      navigateToCart: true
                    });
                  }}
                >
                  Rent This Item
                </Button>
              )}
            </div>

            {/* Custom Request Button */}
            {product.acceptCustomRequests && (
              <div className='flex-1'>
                <Button
                  variant="secondary"
                  className="w-full h-full !border-emerald-600 !text-emerald-700 hover:!bg-emerald-50 px-2"
                  onClick={() => {
                    if (!requireAuth('request from designer')) return;
                    setIsCustomRequestModalOpen(true);
                  }}
                >
                  <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                  </svg>
                  <span className="truncate">Request from Designer</span>
                </Button>
              </div>
            )}
            
            {/* Swap Button (If active) */}
            {openToSwap && (
              <div className='flex-1'>
                <Button
                  variant="offer"
                  className="w-full h-full"
                  onClick={() => {
                    if (!requireAuth('swap items')) return;
                    setActiveTab('swap');
                  }}
                >
                  Swap
                </Button>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Detailed Action Forms - Show when specific action is selected */}

      {activeTab === 'swap' && openToSwap && (
        <div className='mt-6 border-t pt-6'>
          <SwapActions
            product={product}
            onAddToCart={onAddToCart}
          />
        </div>
      )}

      {/* Validation Messages */}
      <ValidationMessage
        message="Please select a rental period first"
        type="warning"
        isVisible={validationErrors.rentalPeriod}
        onDismiss={() => setValidationErrors(prev => ({ ...prev, rentalPeriod: false }))}
        className="mt-4"
      />

      <ValidationMessage
        message="Please select a delivery method first"
        type="warning"
        isVisible={validationErrors.deliveryMethod}
        onDismiss={() => setValidationErrors(prev => ({ ...prev, deliveryMethod: false }))}
        className="mt-4"
      />

      {/* Alert for expensive items */}
      {/* {!isVirtual && price >= 1000 && (
        <div className="mt-6 bg-yellow-50 p-4 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Secure Purchase</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>For high-value purchases, we offer additional verification and insurance options at checkout.</p>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Custom Request Modal */}
      <RequestFromDesignerModal
        isOpen={isCustomRequestModalOpen}
        onClose={() => setIsCustomRequestModalOpen(false)}
        product={product}
      />

      {/* Auth Required Modal */}
      <AuthRequiredModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        message={`Please log in to ${authActionType}`}
        actionType={authActionType}
      />

    </div>
  )
}

export default ProductActions
